import { Handler, SimpleTypedHandler } from './../types/handlers';

export enum ComparisonKeys {
  None = '',
  Equals = '==',
  NotEqual = '!=',
  Begins = '%*',
  Ends = '*%',
  Contains = '%*%',
  IsNumber = '#',
  IsNotNumber = '!#',
  IsPosNumber = '+#',
  IsNegNumber = '-#',
  IsEmpty = '',
  IsNotEmpty = '!!'
}
export enum ComparisonLabels {
  None = '',
  Equals = 'equals',
  NotEqual = 'does not equal',
  Begins = 'begins',
  Ends = 'ends',
  Contains = 'contains',
  IsNumber = 'is a number',
  IsNotNumber = 'is a number',
  IsPosNumber = 'is a positive number',
  IsNegNumber = 'is a negative number',
  IsEmpty = 'is empty',
  IsNotEmpty = 'is not empty'
}
export type Criterion = {
  source: string;
  comparison: ComparisonKeys;
  value: string | boolean;
}
export type CompareInfo = {
  label: ComparisonLabels,
  Match: Handler<Criterion,boolean>,
  requiresValue: boolean;
}
export class Comparisons {
  static [ComparisonKeys.None]: CompareInfo = {
    label:ComparisonLabels.None,
    Match: ()=>{return true;},
    requiresValue:false
  }
  static [ComparisonKeys.Equals]: CompareInfo = {
    label: ComparisonLabels.Equals,
    Match: Comparisons.Equals as Handler<Criterion,boolean>,
    requiresValue: true
  };
  static [ComparisonKeys.NotEqual]: CompareInfo = {
    label: ComparisonLabels.NotEqual,
    Match: Comparisons.NotEqual as Handler<Criterion,boolean>,
    requiresValue: true
  };
  static [ComparisonKeys.Begins]: CompareInfo = {
    label: ComparisonLabels.Begins,
    Match: Comparisons.Begins as Handler<Criterion,boolean>,
    requiresValue: true
  };
  static [ComparisonKeys.Ends]: CompareInfo = {
    label: ComparisonLabels.Ends,
    Match: Comparisons.Ends as Handler<Criterion,boolean>,
    requiresValue: true
  };
  static [ComparisonKeys.Contains]: CompareInfo = {
    label: ComparisonLabels.Contains,
    Match: Comparisons.Contains as Handler<Criterion,boolean>,
    requiresValue: true
  };
  static [ComparisonKeys.IsNumber]: CompareInfo = {
    label: ComparisonLabels.IsNumber,
    Match: Comparisons.IsNumber as Handler<Criterion,boolean>,
    requiresValue: false
  };
  static [ComparisonKeys.IsNotNumber]: CompareInfo = {
    label: ComparisonLabels.IsNotNumber,
    Match: Comparisons.IsNotNumber as Handler<Criterion,boolean>,
    requiresValue: false
  };
  static [ComparisonKeys.IsPosNumber]: CompareInfo = {
    label: ComparisonLabels.IsPosNumber,
    Match: Comparisons.IsPosNumber as Handler<Criterion,boolean>,
    requiresValue: false
  };
  static [ComparisonKeys.IsNegNumber]: CompareInfo = {
    label: ComparisonLabels.IsNegNumber,
    Match: Comparisons.IsNegNumber as Handler<Criterion,boolean>,
    requiresValue: false
  };
  static [ComparisonKeys.IsEmpty]: CompareInfo = {
    label: ComparisonLabels.IsEmpty,
    Match: Comparisons.IsEmpty as Handler<Criterion,boolean>,
    requiresValue: false
  };
  static [ComparisonKeys.IsNotEmpty]: CompareInfo = {
    label: ComparisonLabels.IsNotEmpty,
    Match: Comparisons.IsNotEmpty as Handler<Criterion,boolean>,
    requiresValue: false
  }

  static EscapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }
  static Equals(criterion: Criterion): boolean {
    return criterion.source === criterion.value;
  }
  static NotEqual(criterion:Criterion):boolean {
    return criterion.source !== criterion.value;
  }
  static Begins(criterion: Criterion): boolean {
    return !!criterion.source.match(new RegExp('^' + Comparisons.EscapeRegExp(criterion.value as string)));
  }
  static Ends(criterion: Criterion): boolean {
    return !!criterion.source.match(new RegExp(Comparisons.EscapeRegExp(criterion.value as  string) + '$'))
  }
  static Contains(criterion: Criterion): boolean {
    const matches = criterion.source.match(new RegExp(Comparisons.EscapeRegExp(criterion.value as  string)));
    if (matches && matches.length > 0) {
      return matches[0] == criterion.value;
    }
    return false;
  }
  static IsNumber(criterion: Criterion): boolean {
    const compare = /[\+\-]*[0-9]*\.*[0-9]*/
    const matches = criterion.source.match(compare)
    if (matches && matches.length > 0) {
      return matches[0] === criterion.source;
    }
    return false;
  }
  static IsNotNumber(criterion: Criterion): boolean {
    return !Comparisons.IsNumber(criterion);
  }
  static IsPosNumber(criterion: Criterion): boolean {
    return Comparisons.IsNumber(criterion) && Number(criterion.value) >= 0;
  }
  static IsNegNumber(criterion: Criterion): boolean {
    return !this.IsPosNumber(criterion);
  }
  static IsEmpty(criterion: Criterion): boolean {
    return criterion.source === '';
  }
  static IsNotEmpty(criterion: Criterion): boolean {
    return criterion.source !== '';
  }
  static Compare(criterion: Criterion): boolean {
    const comparison = Comparisons[criterion.comparison];
    return comparison.Match(criterion);
  }
}
